import { Routes } from '@angular/router';
import { AuthGuard } from './app-guard';
import { MenuDisplayPreviewComponent } from './menu-display/components/menu-display-preview/menu-display-preview.component';
import { PreventBackGuardService } from './shared/guards/prevent-back-guard.service';
import { AccountRegistrationComponent } from './account-registration/account-registration.component';
import { DeactivateGuard } from "./shared/guards/deactivate.guard";
import { DomainConstants } from "./shared/constants/domain-constants";
import { Levels } from "./shared/constants/accessLevel";
import { TerminalSelectionComponent } from "./application-login/component/terminal-selection/terminal-selection.component";
import { RouteConfigService } from './shared/services/route-config.service';
import { LAYOUT_DESIGNER_ROUTES } from './layout-designer/layout-designer.routes';
import { INVENTORY_STATES } from './manage-console/inventory/inventory.routes';
import { CONFIGURATOR_CHILDREN } from './configurator/configurator.routes';
import { AUTOMATED_EXPORTS } from './manage-console/automated-exports/automated-exports.routes';
import { INFORMATION_MANAGEMENT_CHILDREN } from './information-management/information-management.routes';
import { MENU_DISPLAY_CONFIG } from './menu-display/menu-display-routes';
import { MANAGEMENT_DASHBOARD_STATES } from './management-dashboard/management-dashboard-routes';

// add deactivate guards to routes that need it
RouteConfigService.addGuardsToRoutes(LAYOUT_DESIGNER_ROUTES);
RouteConfigService.addGuardsToRoutes(INVENTORY_STATES);
RouteConfigService.addGuardsToRoutes(CONFIGURATOR_CHILDREN);
RouteConfigService.addGuardsToRoutes(AUTOMATED_EXPORTS);
RouteConfigService.addGuardsToRoutes(INFORMATION_MANAGEMENT_CHILDREN);
RouteConfigService.addGuardsToRoutes(MENU_DISPLAY_CONFIG.children);

export const routes: Routes = [
  {
    path: 'login',
    canDeactivate: [DeactivateGuard],
    loadChildren: () => import('./application-login/application-login.routes').then(m => m.APPLICATION_LOGIN)
  },
  {
    path: 'manage',
    loadChildren: () => import('./manage-console/manage-console.routes').then(m => m.MANAGE_CONSOLE_STATES)
  },
  {
    path: 'terminals',
    canActivate: [AuthGuard, PreventBackGuardService],
    component: TerminalSelectionComponent
  },
  {
    path: 'menu-display/:id',
    canActivate: [AuthGuard],
    component: MenuDisplayPreviewComponent,
    data: {
      type: DomainConstants.TerminalTypes.MENU_DISPLAY.Name,
      permissions: [{ 'Name': DomainConstants.TerminalTypes.MENU_DISPLAY.Permission, 'Level': Levels.Access }]
    }
  },
  {
    path: 'instruction-terminal',
    canActivate: [AuthGuard],
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.INSTRUCTION_SCREEN.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./instruction-terminal/instruction-terminal.routes').then(m => m.INSTRUCTION_STATES)
  },
  {
    path: 'serve-screen',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.SERVE_SCREEN.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./serve-screen/serve-screen.routes').then(m => m.SERVE_SCREEN_STATES)
  },
  {
    path: 'make-table',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.MAKE_TABLE.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./make-table/make-table.route').then(m => m.MAKE_TABLE_STATES)
  },
  {
    path: 'pick-screen',
    loadChildren: () => import('./pick-screen/pick-screen.route').then(m => m.PICK_SCREEN_STATES)
  },
  {
    path: 'order-confirmation',
    loadChildren: () => import('./order-confirmation/order-confirmation-routes').then(m => m.ORDER_CONFIRMATION_STATES)
  },
  {
    path: 'order-status',
    loadChildren: () => import('./order-status/order-status-routes').then(m => m.ORDER_STATUS_STATES)
  },
  {
    path: 'order',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.ORDER_ENTRY.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./orders/order.routes').then(m => m.ORDER_STATES)
  },
  {
    path: 'digital-signage',
    loadChildren: () => import('./digital-signage/digital-signage-routes').then(m => m.DIGITAL_SIGNAGE_STATES)
  },
  {
    path: 'dj',
    loadChildren: () => import('./dj/dj-routes').then(m => m.DJ_STATES)
  },
  {
    path: 'time-entry',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.TIME_ENTRY.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./time-entry-terminal/time-entry-terminal-routes').then(m => m.TIME_ENTRY_STATES)
  },
  {
    path: 'timer',
    loadChildren: () => import('./timer/timer-routes').then(m => m.TIMER_ROUTES)
  },
  {
    path: 'product-build-queue',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.PRODUCT_BUILD_QUEUE.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./product-build-queue/product-build-queue.routes').then(m => m.PRODUCT_BUILD_STATES)
  },
  {
    path: 'back-office',
    loadChildren: () => import('./back-office/back-office.routes').then(m => m.BACK_OFFICE_STATES)
  },
  {
    path: 'menu-explorer',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.MENU_EXPLORER.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./menu-explorer/menu-explorer.routes').then(m => m.MENU_EXPLORER_STATES)
  },
  {
    path: 'order-kiosk',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.ORDER_KIOSK.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./order-kiosk/order-kiosk.routes').then(m => m.ORDER_KIOSK_STATE)
  },
  {
    path: 'wait-list',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.WAITLIST.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./wait-list/wait-list.routes').then(m => m.WAITLIST_STATES)
  },
  {
    path: 'status-details-screen',
    data: {
      permissions: [{ 'Name': DomainConstants.TerminalTypes.STATUS_DETAIL_SCREEN.Permission, 'Level': Levels.Access }]
    },
    loadChildren: () => import('./status-details-screen/status-details-screen.routes').then(m => m.STATUS_DETAILS_STATES)
  },
  {
    path: 'register',
    component: AccountRegistrationComponent
  },
  ...MANAGEMENT_DASHBOARD_STATES,
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/login',
    pathMatch: 'full'
  }
];
